import type { AppConfigKeys, KvKeys } from '@rialtic/types'
import { featureFlags, parseAppConfig } from '@rialtic/types'
import { acceptHMRUpdate, defineStore } from 'pinia'

// @todo(wfl-6352): These types shouldn't be necessary but the `featureFlags` const is not strictly typed
// using the expected keys from the `AppConfigKeys` enum.
type FeatureFlagKey = keyof typeof featureFlags
type FeatureFlags = Record<FeatureFlagKey, boolean>

export const useAppConfiguration = defineStore('app-config', {
  state: () => {
    return { ...featureFlags } as FeatureFlags
  },

  actions: {
    async getKey(key: AppConfigKeys) {
      const { $datadog } = useNuxtApp()
      const api = useWorkersApi()

      try {
        // @ts-expect-error - figure out why this triggers an `excessive stack depth` type error
        const isSet = (await api.fetchPublic(`/app-config/${key}`)) as
          | string
          | null

        this.$patch({
          [key]: !!isSet,
        })
      } catch (error) {
        $datadog.addError(error, {
          function: 'stores/appConfig:getKey',
        })
      }

      return this.$state
    },

    async getAll() {
      const { $datadog } = useNuxtApp()
      const api = useWorkersApi()

      try {
        const keys: KvKeys[] = await api.fetchPublic('/app-config')

        this.$patch(parseAppConfig(keys) as FeatureFlags)
      } catch (error) {
        $datadog.addError(error, {
          function: 'stores/appConfig:getAll',
        })
      }

      return this.$state
    },

    async toggleKey(key: AppConfigKeys) {
      await this.updateAppConfigKey(key, !this.$state[key as FeatureFlagKey])
      return this.getAll()
    },

    async updateAppConfigKey(key: AppConfigKeys, isSet: boolean) {
      const { $datadog } = useNuxtApp()
      const api = useWorkersApi()

      const prevVal = this.$state[key as FeatureFlagKey]

      this.$patch({
        [key]: isSet,
      })

      try {
        if (isSet) {
          return api.fetch(`/admin/app-config/${key}`, {
            method: 'PUT',
            body: {
              value: 'true',
            },
          })
        }
        return api.fetch(`/admin/app-config/${key}`, {
          method: 'DELETE',
        })
      } catch (error) {
        this.$patch({
          [key]: prevVal,
        })

        $datadog.addError(error, {
          function: 'stores/appConfig:updateAppConfigKey',
        })

        throw error
      }
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useAppConfiguration, import.meta.hot))
